import React, { useEffect, useState } from "react";
import api from "../../services/api";
import { toast } from "react-toastify";
import { FaPencilAlt } from "react-icons/fa";
import { Link } from "react-router-dom";
const Settings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState({
    delivery_charges: 0,
    tax_percentage: 0,
    handling_charge: 0,
    surge_charge: 0,
  });

  // Category GST & Commission Manage States
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [selectedGstPercentage, setSelectedGstPercentage] = useState(0);
  const [selectedCommissionType, setSelectedCommissionType] = useState("percentage");
  const [selectedCommissionValue, setSelectedCommissionValue] = useState(0);
  const [savingCategory, setSavingCategory] = useState(false);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const res = await api.get("/api/settings");
      const data = res.data?.data || {};
      setForm({
        delivery_charges: Number(data.delivery_charges || 0),
        tax_percentage: Number(data.tax_percentage || 0),
        handling_charge: Number(data.handling_charge || 0),
        surge_charge: Number(data.surge_charge || 0),
      });
    } catch (err) {
      console.error(err);
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const res = await api.get("/api/categories");
      const cats = res.data?.data?.categories || [];
      setCategories(cats);
    } catch (err) {
      console.error("Failed to load categories:", err);
      toast.error("Failed to load categories");
    }
  };

  useEffect(() => {
    loadSettings();
    loadCategories();
  }, []);

  const handleCategoryChange = (e) => {
    const catId = e.target.value;
    setSelectedCategoryId(catId);
    if (catId) {
      const category = categories.find((cat) => cat.id === catId);
      if (category) {
        setSelectedGstPercentage(Number(category.gst_percentage || 0));
        setSelectedCommissionType(category.commission_type || "percentage");
        setSelectedCommissionValue(Number(category.commission_value || 0));
      }
    } else {
      setSelectedGstPercentage(0);
      setSelectedCommissionType("percentage");
      setSelectedCommissionValue(0);
    }
  };

  const handleSaveCategoryRates = async () => {
    if (!selectedCategoryId) return;
    try {
      setSavingCategory(true);
      const category = categories.find((cat) => cat.id === selectedCategoryId);
      const payload = {
        name: category.name,
        status: category.status,
        gst_percentage: Number(selectedGstPercentage || 0),
        commission_type: selectedCommissionType,
        commission_value: Number(selectedCommissionValue || 0),
      };
      await api.put(`/api/categories/${selectedCategoryId}`, payload);
      toast.success("Category GST & Commission rates updated successfully");
      await loadCategories();
    } catch (err) {
      console.error(err);
      toast.error("Failed to update category rates");
    } finally {
      setSavingCategory(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put("/api/settings", {
        delivery_charges: Number(form.delivery_charges || 0),
        tax_percentage: Number(form.tax_percentage || 0),
        handling_charge: Number(form.handling_charge || 0),
        surge_charge: Number(form.surge_charge || 0),
      });
      toast.success("Settings saved");
      await loadSettings();
      setIsEditing(false);
    } catch (err) {
      console.error(err);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container mt-2">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <Link className="breadcrum" to="/">
          Home{"/"}
          <i
            className="fa-solid fa-chevron-right"
            style={{ marginLeft: "1px", marginRight: "1px" }}
          ></i>{" "}
          Settings
        </Link>
      </div>
      <div
        className="form-header d-flex justify-content-between align-items-center px-3 py-2 rounded-top"
        style={{ height: "60px" }}
      >
        <h5 className="mb-0 fw-semibold text-light">Settings</h5>

        <div className="d-flex justify-content-between align-items-center mb-0">
          {!isEditing ? (
            <button
              className="btn btn-outline-warning"
              variant="outline-warning"
              onClick={() => setIsEditing(true)}
              disabled={loading}
            >
              <FaPencilAlt />
            </button>
          ) : (
            <div className="d-flex gap-2">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setIsEditing(false);
                  // reload from server to discard local edits
                  loadSettings();
                }}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={saving || loading}
              >
                {saving ? "Saving..." : "Save Settings"}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Delivery Charges</label>
                  <input
                    type="number"
                    className="form-control"
                    name="delivery_charges"
                    value={form.delivery_charges}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    disabled={!isEditing}
                  />
                  <div className="form-text text-muted mx-2">
                    Define the value used to calculate delivery charges per
                    order.
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Tax Percentage</label>
                  <div className="input-group">
                    <input
                      type="number"
                      className="form-control"
                      name="tax_percentage"
                      value={form.tax_percentage}
                      onChange={handleChange}
                      min="0"
                      step="0.01"
                      disabled={!isEditing}
                    />
                    <span className="input-group-text">%</span>
                  </div>
                  <div className="form-text text-muted mx-2">
                    Defines the percentage of tax added per order.
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Handling Charge</label>
                  <input
                    type="number"
                    className="form-control"
                    name="handling_charge"
                    value={form.handling_charge}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    disabled={!isEditing}
                  />
                  <div className="form-text text-muted mx-2">
                    Define the handling charge that will be added when an order is placed.
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Surge Charge</label>
                  <input
                    type="number"
                    className="form-control"
                    name="surge_charge"
                    value={form.surge_charge}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    disabled={!isEditing}
                  />
                  <div className="form-text text-muted mx-2">
                    Define the surge charge that will be added when an order is placed.
                  </div>
                </div>
              </div>
              {/* <small className="text-muted">
                If there are no values in database, defaults will be shown as 0.
              </small> */}
            </>
          )}
        </div>
      </div>

      {/* Product GST & Commission Manage Card */}
      <div
        className="form-header d-flex justify-content-between align-items-center px-3 py-2 rounded-top mt-4"
        style={{ height: "60px" }}
      >
        <h5 className="mb-0 fw-semibold text-light">Product GST & Commission Manage</h5>
        <div>
          <button
            className="btn btn-primary"
            onClick={handleSaveCategoryRates}
            disabled={savingCategory || !selectedCategoryId}
          >
            {savingCategory ? "Saving..." : "Save Category Rates"}
          </button>
        </div>
      </div>
      <div className="card mb-4">
        <div className="card-body">
          <div className="row">
            <div className="col-md-12 mb-3">
              <label className="form-label fw-semibold">Select Category</label>
              <select
                className="form-select form-control"
                value={selectedCategoryId}
                onChange={handleCategoryChange}
              >
                <option value="">-- Choose a Category --</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
              <div className="form-text text-muted mx-2">
                Select a category to view and manage its specific GST & Commission rates.
              </div>
            </div>

            {selectedCategoryId && (
              <>
                <div className="col-md-4 mb-3">
                  <label className="form-label fw-semibold">GST Percentage</label>
                  <div className="input-group">
                    <input
                      type="number"
                      className="form-control"
                      value={selectedGstPercentage}
                      onChange={(e) => setSelectedGstPercentage(e.target.value)}
                      min="0"
                      max="100"
                      step="0.01"
                      required
                    />
                    <span className="input-group-text">%</span>
                  </div>
                  <div className="form-text text-muted mx-2">
                    GST tax rate applied automatically to this category's products.
                  </div>
                </div>

                <div className="col-md-4 mb-3">
                  <label className="form-label fw-semibold">Commission Type</label>
                  <select
                    className="form-select form-control"
                    value={selectedCommissionType}
                    onChange={(e) => setSelectedCommissionType(e.target.value)}
                  >
                    <option value="percentage">Percentage based (%)</option>
                    <option value="fixed">Fixed Amount</option>
                  </select>
                  <div className="form-text text-muted mx-2">
                    Determine whether platform commission is a fixed fee or percentage.
                  </div>
                </div>

                <div className="col-md-4 mb-3">
                  <label className="form-label fw-semibold">
                    Commission Value {selectedCommissionType === "percentage" ? "(%)" : "(Amount)"}
                  </label>
                  <div className="input-group">
                    <input
                      type="number"
                      className="form-control"
                      value={selectedCommissionValue}
                      onChange={(e) => setSelectedCommissionValue(e.target.value)}
                      min="0"
                      step="0.01"
                      required
                    />
                    {selectedCommissionType === "percentage" && <span className="input-group-text">%</span>}
                  </div>
                  <div className="form-text text-muted mx-2">
                    The commission rate/fee value charged by the platform.
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
